/**
 * COMMERCE-CANARY-163A-R1A — merge allowlisted canary products into Store V2 catalog.
 * Source: /api/store-v2/canary-products (Pages → Worker). Never Prom.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { buildSearchHaystack } from "./search-v2.js";
import { tileName } from "./taxonomy-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import { formatUah } from "./money-v2.js";

export type CanaryCatalogProductRow = {
  id: string;
  sku: string;
  title_uk: string;
  title_en: string;
  category_id: string;
  category_slug: string;
  price_minor: number;
  currency: string;
  max_quantity: number;
  badge: string;
  canary: boolean;
  prom_export: number;
  analytics_excluded: boolean;
  categoryKeys: string[];
  sort_priority?: number;
  cross_sell_peer_sku?: string;
  /** COMMERCE-LAUNCH-168D — patch price on an existing Prom product; keep media/title. */
  price_override_only?: boolean;
  commerce_launch_168d?: boolean;
};

export type CanaryProductsFile = {
  ok: boolean;
  schema_version: "canary_products_v1";
  visible: boolean;
  currency: "UAH";
  badge: string;
  prom_export: number;
  analytics_excluded: boolean;
  products: CanaryCatalogProductRow[];
};

let cache: Promise<CanaryProductsFile | null> | null = null;

export function invalidateCanaryProductsCache(): void {
  cache = null;
  resolvedCanaryFile = null;
}

async function fetchCanaryProducts(): Promise<CanaryProductsFile | null> {
  try {
    const res = await fetch(`/api/store-v2/canary-products?t=${Date.now()}`, {
      headers: storeV2AuthHeaders({ accept: "application/json" }),
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as CanaryProductsFile;
    if (json?.schema_version !== "canary_products_v1") return null;
    if (!json.visible || !json.products?.length) return json;
    return json;
  } catch {
    return null;
  }
}

let resolvedCanaryFile: CanaryProductsFile | null = null;

export async function loadCanaryProducts(): Promise<CanaryProductsFile | null> {
  if (!cache) {
    cache = fetchCanaryProducts().then((file) => {
      resolvedCanaryFile = file;
      return file;
    });
  }
  return cache;
}

/** COMMERCE-176B-R1 — always null: bank canary is not a Product.price. */
export function getResolvedCanaryPriceUah(_productId: string): number | null {
  void _productId;
  return null;
}

export function getResolvedCanaryBadge(productId: string): string | null {
  const row = resolvedCanaryFile?.products?.find((p) => p.id === productId);
  return row?.badge ?? (resolvedCanaryFile?.badge ?? null);
}

function rowToProduct(row: CanaryCatalogProductRow): CatalogV2Product {
  const priceUah = row.price_minor / 100;
  const categoryName = tileName(row.category_id, "ua");
  const product: CatalogV2Product = {
    id: row.id,
    sku: row.sku,
    title: row.title_uk,
    titleRu: null,
    categoryKeys: row.categoryKeys?.length ? row.categoryKeys : [row.category_id],
    categoryName,
    imageUrl: null,
    productUrl: null,
    priceUah,
    displayPrice: formatUah(priceUah),
    isPriced: true,
    stockStatus: "in_stock",
    isSellable: true,
    sellabilityReason: "canary_staging",
    brand: null,
    country: null,
    weight: null,
    regularPriceUah: null,
    salePriceUah: null,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: null,
    sourceRevision: "canary_products_v1",
    search: "",
    promProductId: null,
    merchantSku: row.sku,
    merchantArticle: null,
    barcodeGtin: null,
    barcodeVerification: "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: "commerce_canary_163a",
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: "2026-07-01T00:00:00.000Z",
    catalogArrivedAt: "2026-07-01T00:00:00.000Z",
    sourceTitle: row.title_uk,
    customerTitle: row.title_uk,
    titleAliases: [],
    canaryBadge: row.badge,
    maxOrderQty: row.max_quantity,
    canarySortPriority: row.sort_priority ?? 999,
    canaryCrossSellPeerSku: row.cross_sell_peer_sku ?? null,
  };
  product.search = buildSearchHaystack({
    title: product.title,
    titleRu: product.titleRu,
    categoryName: product.categoryName,
    sku: product.merchantSku ?? product.sku,
    brand: product.brand,
    country: product.country,
    promProductId: product.promProductId,
    aliases: product.titleAliases,
  });
  return product;
}

export async function enrichCatalogWithCanaryProducts(
  products: CatalogV2Product[],
): Promise<CatalogV2Product[]> {
  // BANK-IBAN-R3.10 — canary is not a Product domain. Never merge 0.01 / 168D onto catalog ids.
  void products;
  return products;
}

export function isCanaryCatalogProduct(product: CatalogV2Product): boolean {
  return product.sourceSystem === "commerce_canary_163a";
}
